/**
 * My Stickers grid. Spec §8.
 *
 * Display rules from §8:
 *   - **Silhouettes** for streak milestones and totals — unearned ones
 *     still visible (dimmed) because they're long-term motivation.
 *   - **Dimmed, but never hidden** for school years — Eva already knows
 *     which year she's in, so there's no surprise to protect. An unearned
 *     one shows its full art at 50% with the caption readable.
 *   - **Hidden** for firsts and mastery — surprise rewards feel
 *     magical when they show up unexpectedly.
 *   - **Per-session** stickers (Full Marks! / Wrote It Out / Better Than
 *     Yesterday) show with a "× N" tally and a "new!" tag if any
 *     occurrences haven't been viewed yet.
 *
 * On mount we also POST /api/achievements/seen for every visible
 * earned-but-unseen sticker — the act of seeing the grid is what
 * clears the "new!" badge for the next visit.
 *
 * Real sticker artwork is Phase 8 work; the shapes here are
 * token-styled placeholders that match design.md §6 shape language.
 */
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/Button";
import { ExplainableSticker } from "@/components/ExplainableSticker";
import { MasteryCarousel } from "@/components/MasteryCarousel";
import { StreakCounter } from "@/components/StreakCounter";
import type { StickerCategory } from "@/components/StickerEmblem";
import { useNavigation } from "@/context/NavigationContext";
import { api } from "@/lib/api";
import type {
  Achievement,
  AchievementCategory,
  PerSessionStickerTally,
} from "@/types/data";
import "./MyStickersScreen.css";

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      achievements: Achievement[];
      perSession: PerSessionStickerTally[];
      /** Live daily-streak count from /api/streak. null = couldn't load
       *  (the streak fetch failed); we hide the counter rather than
       *  show a misleading 0. */
      currentStreak: number | null;
    };

const CATEGORY_LABEL: Record<AchievementCategory | "per_session", string> = {
  school: "School years",
  first: "Firsts",
  per_session: "Each session",
  streak: "Streaks",
  total: "Totals",
  mastery: "Mastery",
};

export function MyStickersScreen() {
  const { navigate } = useNavigation();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Stickers and the live streak count come from two endpoints. The
      // stickers are essential; the streak is a nice-to-have on this
      // screen, so a failed streak fetch shouldn't blank the page.
      const [achRes, streakRes] = await Promise.all([
        api.listAchievements(),
        api.getStreak(),
      ]);
      if (cancelled) return;
      if (achRes.status !== 200 || !achRes.body) {
        setState({ kind: "error", message: "Couldn't load your stickers." });
        return;
      }
      const currentStreak =
        streakRes.status === 200 && streakRes.body ? streakRes.body.currentStreak : null;
      setState({
        kind: "ready",
        achievements: achRes.body.achievements,
        perSession: achRes.body.perSessionTallies,
        currentStreak,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Mark seen on view — runs once when state flips to ready.
  // Intentionally keys on state.kind only: re-running this when the
  // optimistic local flip below changes state.achievements would loop
  // (every re-render would re-POST a now-empty list, which is harmless
  // but wasteful, plus the eslint warning is loud).
  useEffect(() => {
    if (state.kind !== "ready") return;
    const oneTimeIds = state.achievements
      .filter((a) => a.unlockedAt !== null && !a.seenByUser)
      .map((a) => a.id);
    // Per-session: we don't have row-ids here, just tally counts. The
    // seen endpoint takes specific row ids, so opening My Stickers
    // doesn't clear per-session unseen counts — they're dismissed by
    // the celebration sign-off instead. (One-time badges still clear
    // here.)
    if (oneTimeIds.length === 0) return;
    void api.markAchievementsSeen({ oneTimeIds });
    setState((s) =>
      s.kind === "ready"
        ? {
            ...s,
            achievements: s.achievements.map((a) =>
              oneTimeIds.includes(a.id) ? { ...a, seenByUser: true } : a,
            ),
          }
        : s,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.kind]);

  const groups = useMemo(() => {
    if (state.kind !== "ready") return null;
    const byCat = new Map<AchievementCategory, Achievement[]>();
    for (const a of state.achievements) {
      const list = byCat.get(a.category) ?? [];
      list.push(a);
      byCat.set(a.category, list);
    }
    return byCat;
  }, [state]);

  if (state.kind === "loading") {
    return (
      <main className="stickers">
        <p className="stickers__loading">Loading your stickers…</p>
      </main>
    );
  }
  if (state.kind === "error") {
    return (
      <main className="stickers">
        <p className="stickers__error">{state.message}</p>
        <Button onClick={() => navigate("home")}>Back to home</Button>
      </main>
    );
  }

  const schoolYears = groups?.get("school") ?? [];
  const firsts = groups?.get("first") ?? [];
  const streaks = groups?.get("streak") ?? [];
  const totals = groups?.get("total") ?? [];
  const mastery = groups?.get("mastery") ?? [];
  const masteredWords = mastery.filter((a) => a.unlockedAt !== null);

  return (
    <main className="stickers">
      <header className="stickers__header">
        <h1 className="stickers__title">My Stickers</h1>
        <Button size="small" variant="secondary" onClick={() => navigate("home")}>
          Back
        </Button>
      </header>

      {state.currentStreak !== null && (
        <StreakCounter days={state.currentStreak} variant="card" />
      )}

      {/* School years — always visible; unearned ones dim rather than
          turning into silhouettes (see the display rules above). */}
      {schoolYears.length > 0 && (
        <Section title={CATEGORY_LABEL.school}>
          <Grid>
            {schoolYears.map((a) => (
              <Sticker
                key={a.id}
                stickerId={a.id}
                category={a.category}
                name={a.name}
                isNew={a.unlockedAt !== null && !a.seenByUser}
                locked={a.unlockedAt === null}
              />
            ))}
          </Grid>
        </Section>
      )}

      {/* Firsts — hidden when locked */}
      <Section title={CATEGORY_LABEL.first}>
        {firsts.filter((a) => a.unlockedAt !== null).length === 0 ? (
          <EmptyHint>You&rsquo;ll find your first stickers here once you earn them.</EmptyHint>
        ) : (
          <Grid>
            {firsts
              .filter((a) => a.unlockedAt !== null)
              .map((a) => (
                <Sticker
                  key={a.id}
                  stickerId={a.id}
                  category={a.category}
                  name={a.name}
                  isNew={!a.seenByUser}
                />
              ))}
          </Grid>
        )}
      </Section>

      {/* Per-session — render all three with tally counts */}
      <Section title={CATEGORY_LABEL.per_session}>
        <Grid>
          {state.perSession.map((p) => (
            <Sticker
              key={p.id}
              stickerId={p.id}
              category="per_session"
              name={p.name}
              count={p.count}
              isNew={p.unseenCount > 0}
              locked={p.count === 0}
            />
          ))}
        </Grid>
      </Section>

      {/* Streaks — silhouettes when locked */}
      <Section title={CATEGORY_LABEL.streak}>
        <Grid>
          {streaks.map((a) => (
            <Sticker
              key={a.id}
              stickerId={a.id}
              category={a.category}
              name={a.name}
              isNew={a.unlockedAt !== null && !a.seenByUser}
              locked={a.unlockedAt === null}
              silhouette={a.unlockedAt === null}
            />
          ))}
        </Grid>
      </Section>

      {/* Totals — silhouettes when locked */}
      <Section title={CATEGORY_LABEL.total}>
        <Grid>
          {totals.map((a) => (
            <Sticker
              key={a.id}
              stickerId={a.id}
              category={a.category}
              name={a.name}
              isNew={a.unlockedAt !== null && !a.seenByUser}
              locked={a.unlockedAt === null}
              silhouette={a.unlockedAt === null}
            />
          ))}
        </Grid>
      </Section>

      {/* Mastery — hidden when locked, surfaced when unlocked. Per
          spec §8 mastery stickers are lazily inserted into the
          achievements table only once a word hits the threshold (5
          consecutive first-pass-correct), so the unfiltered list
          here is exactly "words she's mastered".

          The collection grows without bound and every emblem is the
          same hexagon template, so instead of a flat grid we show a
          single large count + a swipeable carousel (MasteryCarousel). */}
      {masteredWords.length > 0 && (
        <Section title={CATEGORY_LABEL.mastery}>
          <MasteryCarousel items={masteredWords} />
        </Section>
      )}
    </main>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="stickers__section">
      <h2 className="stickers__section-title">{title}</h2>
      {children}
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="stickers__grid">{children}</div>;
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return <p className="stickers__empty">{children}</p>;
}

function Sticker({
  stickerId,
  category,
  name,
  count,
  isNew = false,
  locked = false,
  silhouette = false,
}: {
  stickerId: string;
  category: StickerCategory;
  name: string;
  count?: number;
  isNew?: boolean;
  locked?: boolean;
  silhouette?: boolean;
}) {
  // Sized by .stickers__cell wrapper; emblem fills it.
  // Defaults applied at the destructure so we pass concrete booleans
  // down — exactOptionalPropertyTypes rejects `boolean | undefined`.
  return (
    <div className="stickers__cell">
      <ExplainableSticker
        stickerId={stickerId}
        category={category}
        name={name}
        {...(count !== undefined && { count })}
        isNew={isNew}
        locked={locked}
        silhouette={silhouette}
      />
    </div>
  );
}
